// HighlightManager — the map-object gold-highlight Facade (map-architecture Phase 6, P5).
//
// A plain TypeScript class (NOT React). Absorbs the two functions that used to
// live at MapCanvas component scope and were called from 9 different places
// across various effects/handlers (P5 — "highlight shared closure з 9 call-сайтів"):
//   * createHighlightForMapObject → `apply(mapObject, marker, strokeMultiplier?)`
//   * runPurchaseHouseHighlight   → `runPurchaseAnim(purchaseId, markersReady,
//                                     isPixiAppReady, userData)`
//
// The bodies are moved VERBATIM from MapCanvas; the only change is closure
// variable → context access. Values that are reactive (change every render —
// `markersReady`, `isPixiAppReady`, `userData`) are NOT baked into the
// constructor context (this instance is created once via a `useRef` guard and
// would otherwise see a stale first-render snapshot); they're passed as call-time
// parameters exactly as `runPurchaseHouseHighlight`'s caller already had them in
// scope, preserving the original closure-capture timing byte-for-byte.
import * as PIXI from 'pixi.js';
import { MapObject } from '../../../models/mapObject';
import type { UserData } from '../../../models/user';
import type { MapModalName, MapModalPayload } from '../../../stores/useMapModalStore';
import { mapSceneSingleton } from '../mapScene';
import type { TickerBoundContainer } from '../render/mapPixiExtensions';

export interface HighlightManagerContext {
  scene: import('../mapSceneState').MapSceneState;
  setSelectedMapObject: (value: MapObject | null) => void;
  openModal: (modal: MapModalName, payload?: Partial<MapModalPayload>) => void;
  REAL_ESTATE_FIXED_SIZES: Record<number, { width: number; height: number }>;
  STATIC_OBJECT_FIXED_SIZES: Record<string, { width: number; height: number }>;
  PLOT_MARKER_SIZE: { width: number; height: number };
  /** Shared camera animator — keeps purchase fly-to from racing other cameras. */
  animateCamera: (
    params: { position?: { x: number; y: number }; scale?: number; time?: number; ease?: string },
    opts?: { force?: boolean },
  ) => void;
}

export class HighlightManager {
  private ctx: HighlightManagerContext;

  // Only ever consumed inside `apply` in the original code — moves with it.
  private readonly HIGHLIGHTABLE_TYPES = new Set(['plot', 'house', 'cityhall', 'shop', 'hell_of_flame']);

  constructor(ctx: HighlightManagerContext) {
    this.ctx = ctx;
  }

  // ── createHighlightForMapObject (verbatim body, closure → this.ctx) ────────
  apply = (mapObject: MapObject, marker: PIXI.Container, strokeMultiplier = 1): void => {
    const {
      scene,
      REAL_ESTATE_FIXED_SIZES, STATIC_OBJECT_FIXED_SIZES, PLOT_MARKER_SIZE,
    } = this.ctx;
    // No gold highlight in god/admin mode
    if (scene.flags.godModeActive) return;
    if (!scene.layers.highlight || !scene.pixi.app) return;
    // Only highlight free plots and buildings — skip decorations, trees, islands, etc.
    if (!this.HIGHLIGHTABLE_TYPES.has(mapObject.type)) return;

    const containerX = marker.x;
    const containerY = marker.y;

    const sprite = marker.children[0] as PIXI.Sprite;
    let spriteWidth = 0;
    let spriteHeight = 0;
    let anchorX = 0.5;
    let anchorY = 0.5;

      if (sprite && sprite.texture) {
        if (mapObject.type === 'cityhall' || mapObject.type === 'shop' || mapObject.type === 'hell_of_flame') {
          const fixedSize = STATIC_OBJECT_FIXED_SIZES[mapObject.type];
          if (fixedSize) {
            spriteWidth = fixedSize.width;
            spriteHeight = fixedSize.height;
          } else {
            console.error(`[MapCanvas] No fixed size for ${mapObject.type}`);
            return;
          }
          anchorX = 0.5;
          anchorY = 0.5;
        } else if (mapObject.type === 'house') {
          const realEstateId = mapObject.data?.real_estate_id;
          const fixedSize = realEstateId ? REAL_ESTATE_FIXED_SIZES[realEstateId] : null;
          if (!fixedSize) {
            console.error(`[MapCanvas] No fixed size for house with real_estate_id ${realEstateId}`);
            return;
          }
          spriteWidth = fixedSize.width;
          spriteHeight = fixedSize.height;
          anchorX = 0.5;
          anchorY = 0.5;
        } else if (mapObject.type === 'plot') {
          // Використовуємо фактичний розмір спрайта (з урахуванням масштабів)
          spriteWidth = sprite.width || PLOT_MARKER_SIZE.width;
          spriteHeight = sprite.height || PLOT_MARKER_SIZE.height;
          anchorX = sprite.anchor?.x ?? 0.5;
          anchorY = sprite.anchor?.y ?? 1.0;
        } else if (mapObject.type.startsWith('decor_')) {
          // Для decor об'єктів використовуємо фактичний розмір спрайта
          spriteWidth = sprite.width;
          spriteHeight = sprite.height;
          anchorX = sprite.anchor?.x ?? 0.5;
          anchorY = sprite.anchor?.y ?? 0.5;
        }
      }

    if (spriteWidth === 0 || spriteHeight === 0) return;

    const centerXLocal = -(anchorX - 0.5) * spriteWidth;
    const centerYLocal = -(anchorY - 0.5) * spriteHeight;

    // Isometric 2.5D ground-footprint diamond.
    // The bottom of the sprite is where the building meets the ground plane.
    // Diamond uses the standard iso 2:1 ratio: half-height = half-width / 2.
    // pad expands the diamond outward on all sides for a bit of breathing room.
    const pad = 14;
    const isoHalfW = spriteWidth / 2 + pad;
    const isoHalfH = spriteWidth / 4 + pad / 2;
    const groundBottomY = centerYLocal + spriteHeight / 2;
    // For plots: anchor bottom (sprite tip) IS the house center → center diamond there.
    // For all other objects: diamond center sits one isoHalfH above the sprite bottom.
    const groundCenterY = mapObject.type === 'plot' ? 0 : groundBottomY - isoHalfH;
    const isoPts = [
      centerXLocal,              groundCenterY - isoHalfH, // top
      centerXLocal + isoHalfW,  groundCenterY,             // right
      centerXLocal,              groundCenterY + isoHalfH, // bottom
      centerXLocal - isoHalfW,  groundCenterY,             // left
    ];

    const highlightContainer = new PIXI.Container();
    highlightContainer.position.set(containerX, containerY);

    const layers = [
      { width: 10 * strokeMultiplier, alpha: 0.3, color: 0xFFD700 },
      { width: 8  * strokeMultiplier, alpha: 0.5, color: 0xFFE44D },
      { width: 6  * strokeMultiplier, alpha: 0.8, color: 0xFFD700 },
    ];

    layers.forEach((layer) => {
      const highlightGraphics = new PIXI.Graphics();
      highlightGraphics.poly(isoPts, true);
      highlightGraphics.stroke({ width: layer.width, color: layer.color, alpha: layer.alpha });
      highlightContainer.addChild(highlightGraphics);
    });

    if (scene.pixi.app) {
      let time = 0;
      const animateGlow = () => {
        if (!highlightContainer.parent) {
          return;
        }

        time += 0.05;
        const pulse = Math.sin(time) * 0.3 + 0.7;

        highlightContainer.children.forEach((child, index) => {
          if (child instanceof PIXI.Graphics) {
            const layer = layers[index];
            if (layer) {
              child.alpha = layer.alpha * pulse;
            }
          }
        });
      };

      scene.pixi.app.ticker.add(animateGlow);
      (highlightContainer as TickerBoundContainer).tickerFunction = animateGlow;
    }

    scene.layers.highlight.addChild(highlightContainer);
    scene.sprites.mapObjectHighlight = highlightContainer;
    mapSceneSingleton.mapObjectHighlight = highlightContainer;
  };

  // ── runPurchaseHouseHighlight (verbatim body, closure → this.ctx/params) ───
  /** Імперативно: підсвітка + zoom + модалка будинку. Повертає cleanup таймера модалки або null, якщо ще не готово. */
  runPurchaseAnim = (
    purchaseId: number,
    markersReady: boolean,
    isPixiAppReady: boolean,
    userData: UserData | undefined,
  ): (() => void) | null => {
    const { scene, setSelectedMapObject, openModal } = this.ctx;
    if (!markersReady || !isPixiAppReady || !scene.layers.highlight || !scene.pixi.app) {
      return null;
    }

    const houseMarker = scene.markers.realEstate.get(purchaseId);
    const houseObj = scene.data.mapObjects.find((o) => o.id === purchaseId);
    if (!houseMarker || houseMarker.destroyed || !houseObj || houseObj.type !== 'house') {
      return null;
    }

    if (scene.sprites.mapObjectHighlight) {
      const hl = scene.sprites.mapObjectHighlight as TickerBoundContainer;
      if (scene.pixi.app && hl.tickerFunction) {
        scene.pixi.app.ticker.remove(hl.tickerFunction);
      }
      scene.layers.highlight.removeChild(scene.sprites.mapObjectHighlight);
      scene.sprites.mapObjectHighlight.destroy();
      scene.sprites.mapObjectHighlight = null;
      mapSceneSingleton.mapObjectHighlight = null;
    }
    this.apply(houseObj, houseMarker, 2);

    // Smooth fly-to via CameraController (force: interrupt any in-flight anim).
    // Was a raw viewport.animate that raced the random initial-house camera —
    // felt like a snap onto the house then a zoom-out.
    this.ctx.animateCamera(
      {
        position: { x: houseObj.x, y: houseObj.y },
        scale: 1.0,
        time: 1100,
        ease: 'easeInOutCubic',
      },
      { force: true },
    );

    // Open modal after the camera settles
    const modalTimer = window.setTimeout(() => {
      setSelectedMapObject(houseObj);
      const hl = scene.data.listingsByHouseId.get(purchaseId);
      if (hl && houseObj.owner_id && userData?.user_id && String(houseObj.owner_id) !== userData.user_id) {
        openModal('salePreview', { salePreviewListing: hl });
      } else {
        openModal('realEstateInfo');
      }
    }, 1150);

    return () => {
      clearTimeout(modalTimer);
    };
  };
}
